/**
 * API client for Smart Link claims, proxied through pro-api to user-service.
 */

import { authenticatedProApiFetch } from './pro-api.client';
import type { ListParams, ListResult } from '../../../ui/api/listApi.types';

export type SmartLinkLinkedVia =
  | 'backfill'
  | 'smart-signup'
  | 'login-onboarding'
  | 'registration-onboarding'
  | 'admin-import'
  | 'google-onboarding'
  | null;

export interface SmartLinkClaimLinkedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface SmartLinkClaim {
  id: string;
  email: string;
  smartUserId: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  phoneVerified: boolean;
  globalPermissions: string[];
  globalRoles: string[];
  linkedUserId: string | null;
  linkedUser: SmartLinkClaimLinkedUser | null;
  linkEnabled: boolean;
  linkedVia: SmartLinkLinkedVia;
  linkedAt: string | null;
  linkDisabledAt: string | null;
  sourceUpdatedAt: string | null;
  lastSyncedAt: string | null;
  /** Cheap, pre-computed sync-status badge signal — see SmartLinkSyncStatus (user-service db/schema.ts). */
  syncStatus: "in_sync" | "out_of_sync" | "unknown";
  syncStatusCheckedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

function buildSmartLinkClaimsQuery(params: ListParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  if (params.sort_by) qs.set('sort_by', params.sort_by);
  if (params.order) qs.set('order', params.order);
  return qs.toString();
}

/** Paginated Smart Link claims list (server-side search/sort/pagination). */
export async function fetchSmartLinkClaims(
  token: string,
  params: ListParams,
): Promise<ListResult<SmartLinkClaim>> {
  const query = buildSmartLinkClaimsQuery(params);
  const data = await authenticatedProApiFetch<{ claims: SmartLinkClaim[]; total: number }>(
    `/smart-link/claims?${query}`,
    token,
  );
  return { data: data.claims, total: data.total };
}

export interface SmartLinkImportEnvironment {
  id: 'primary' | 'alt';
  label: string;
}

/** Which Smart environments are configured — used by the Full Sync modal's environment picker. */
export async function fetchSmartLinkImportEnvironments(
  token: string,
): Promise<SmartLinkImportEnvironment[]> {
  const res = await authenticatedProApiFetch<{ environments: SmartLinkImportEnvironment[] }>(
    '/smart-link/import/environments',
    token,
  );
  return res.environments;
}

export interface SmartLinkFullSyncPreviewItem {
  smartUserId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  /** `replace` swaps out a stale unlinked claim with this email; `conflict` rows can't be committed. */
  kind: "new" | "replace" | "conflict";
  existingClaim: { id: string; smartUserId: string; linked: boolean } | null;
  /** Informational only — Full Sync never auto-links, see the preview modal's copy. */
  existingProUserMatch: { id: string; email: string } | null;
}

export interface SmartLinkFullSyncPreview {
  environment: "primary" | "alt";
  count: number;
  items: SmartLinkFullSyncPreviewItem[];
}

/** Dry run: every Smart user Pro doesn't have a claim for yet. Writes nothing. */
export async function previewSmartLinkFullSync(
  token: string,
  environment: "primary" | "alt",
): Promise<SmartLinkFullSyncPreview> {
  return authenticatedProApiFetch<SmartLinkFullSyncPreview>("/smart-link/full-sync/preview", token, {
    method: "POST",
    body: JSON.stringify({ environment }),
  });
}

/** Creates new (unlinked) claims for the given smartUserIds — omit to commit every currently-unpulled user. */
export async function commitSmartLinkFullSync(
  token: string,
  environment: "primary" | "alt",
  smartUserIds?: string[],
): Promise<{ created: number; replaced: number; skipped: number; requested: number }> {
  return authenticatedProApiFetch<{ created: number; replaced: number; skipped: number; requested: number }>(
    "/smart-link/full-sync/commit",
    token,
    { method: "POST", body: JSON.stringify({ environment, smartUserIds }) },
  );
}

/** Hard-deletes claims. A linked claim's Pro user is kept, just unlinked from Smart. */
export async function deleteSmartLinkClaims(
  token: string,
  ids: string[],
): Promise<{ deleted: number; requested: number }> {
  return authenticatedProApiFetch<{ deleted: number; requested: number }>("/smart-link/claims/delete", token, {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

export interface SmartLinkCompareField {
  field:
    | 'email'
    | 'firstName'
    | 'lastName'
    | 'phone'
    | 'phoneVerified'
    | 'emailVerified'
    | 'globalRoles'
    | 'globalPermissions';
  proValue: unknown;
  smartValue: unknown;
  matches: boolean;
}

export interface SmartLinkComparison {
  claimId: string;
  linked: boolean;
  /** False if Smart couldn't be reached live — smartValue falls back to the last-synced snapshot. */
  smartFetchedLive: boolean;
  smartError: string | null;
  fields: SmartLinkCompareField[];
}

/** Live side-by-side field comparison for one claim — Smart values fetched from the source, not cached. */
export async function fetchSmartLinkClaimComparison(
  token: string,
  claimId: string,
): Promise<SmartLinkComparison> {
  return authenticatedProApiFetch<SmartLinkComparison>(
    `/smart-link/claims/${encodeURIComponent(claimId)}/compare`,
    token,
  );
}

/** Toggle a claim's link on/off -- suppresses sync in both directions without deleting the claim. */
export async function setSmartLinkClaimEnabled(
  token: string,
  claimId: string,
  enabled: boolean,
): Promise<{ linkEnabled: boolean; linkDisabledAt: string | null }> {
  return authenticatedProApiFetch<{ linkEnabled: boolean; linkDisabledAt: string | null }>(
    `/smart-link/claims/${encodeURIComponent(claimId)}/link-enabled`,
    token,
    { method: 'PATCH', body: JSON.stringify({ enabled }) },
  );
}

/** Manually re-push this claim's linked Pro user's current roles/permissions/profile/password to Smart. */
export async function syncSmartLinkClaimFromPro(
  token: string,
  claimId: string,
): Promise<{ triggered: true }> {
  return authenticatedProApiFetch<{ triggered: true }>(
    `/smart-link/claims/${encodeURIComponent(claimId)}/sync-from-pro`,
    token,
    { method: 'POST' },
  );
}

/** Manually re-push this claim's linked Smart user's current roles/permissions/profile to Pro. */
export async function syncSmartLinkClaimFromSmart(
  token: string,
  claimId: string,
): Promise<{ triggered: true; failedSteps: number }> {
  return authenticatedProApiFetch<{ triggered: true; failedSteps: number }>(
    `/smart-link/claims/${encodeURIComponent(claimId)}/sync-from-smart`,
    token,
    { method: 'POST' },
  );
}

// ─── Health + scheduled jobs (shared by every Smart Link tab) ────────────────

export function formatCategoryCounts(counts: Record<string, number> | undefined): string {
  return Object.entries(counts ?? {})
    .sort((a, b) => b[1] - a[1])
    .map(([type, n]) => `${type} (${n})`)
    .join(", ");
}

export interface SmartLinkHealth {
  entity: "users" | "products";
  linked: { inSync: number; outOfSync: number; unknown: number; disabled: number };
  awaitingLink?: number;
  unlinkedInSmart: number | null;
  configured?: boolean;
  openFailures: number;
  excluded: { proOnly: number; simulated: number; unsupportedInSmart?: Record<string, number> };
  lastReconciledAt: string | null;
  lastReconcileStatus: "running" | "succeeded" | "failed" | null;
  lastReconcileError: string | null;
}

export async function fetchSmartLinkUserHealth(token: string): Promise<SmartLinkHealth> {
  return authenticatedProApiFetch<SmartLinkHealth>("/smart-link/health", token);
}

export async function fetchSmartLinkProductHealth(token: string): Promise<SmartLinkHealth> {
  return authenticatedProApiFetch<SmartLinkHealth>("/smart-link/products/health", token);
}

export interface ScheduledJob {
  service: "user-service" | "product-service";
  name: string;
  description: string;
  schedule: string;
  enabled: boolean;
  running: boolean;
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastStatus: "running" | "succeeded" | "failed" | null;
  lastError: string | null;
  lastDurationMs: number | null;
}

export const SMART_LINK_RECONCILE_JOBS = {
  users: { service: "user-service", name: "smart-link-users-reconcile" },
  products: { service: "product-service", name: "smart-link-products-reconcile" },
} as const;

export async function fetchScheduledJobs(token: string): Promise<ScheduledJob[]> {
  const res = await authenticatedProApiFetch<{ jobs: ScheduledJob[] }>("/scheduled-jobs", token);
  return res.jobs;
}

export async function runScheduledJob(
  token: string,
  service: ScheduledJob["service"],
  name: string,
): Promise<void> {
  await authenticatedProApiFetch(`/scheduled-jobs/${service}/${encodeURIComponent(name)}/run`, token, {
    method: "POST",
  });
}

// ─── Products ────────────────────────────────────────────────────────────────

export type SmartLinkSyncStatus = "in_sync" | "out_of_sync" | "unknown";

export interface SmartLinkProductLink {
  id: string;
  productId: string;
  smartProductId: string;
  environment: "primary" | "alt";
  linkEnabled: boolean;
  linkedVia: "backfill" | "import" | "full-sync";
  linkedAt: string;
  linkDisabledAt: string | null;
  sourceUpdatedAt: string | null;
  lastSyncedAt: string | null;
  syncStatus: SmartLinkSyncStatus;
  syncStatusCheckedAt: string | null;
  product: { id: string; name: string; serial: string | null; status: string };
}

export async function fetchSmartLinkProducts(
  token: string,
  params: ListParams,
): Promise<ListResult<SmartLinkProductLink>> {
  const qs = new URLSearchParams();
  qs.set("page", String(params.page));
  qs.set("limit", String(params.limit));
  if (params.search) qs.set("search", params.search);
  if (params.order) qs.set("order", params.order);
  const data = await authenticatedProApiFetch<{ links: SmartLinkProductLink[]; total: number }>(
    `/smart-link/products?${qs.toString()}`,
    token,
  );
  return { data: data.links, total: data.total };
}

export async function fetchSmartLinkProductEnvironments(
  token: string,
): Promise<SmartLinkImportEnvironment[]> {
  const res = await authenticatedProApiFetch<{ environments: SmartLinkImportEnvironment[] }>(
    "/smart-link/products/environments",
    token,
  );
  return res.environments;
}

export type SmartLinkModelResolution =
  | { kind: "resolved"; productModelId: string; via: "generation" | "category_product" | "product_line" }
  | { kind: "ambiguous"; candidates: Array<{ id: string; name: string; productLine: string }> }
  | { kind: "unmapped"; reason: "no_category" | "unknown_category" | "no_active_model" };

export interface SmartLinkProductPreviewItem {
  smartProductId: string;
  name: string | null;
  serial: string | null;
  categoryType: string | null;
  archived: boolean;
  isSimulated: boolean;
  componentCount: number;
  model: SmartLinkModelResolution;
  existingProProduct: { id: string; name: string } | null;
  conflicts: Array<{ field: "serial" | "macAddress" | "component"; value: string; productId: string | null; productName: string }>;
}

export async function previewSmartLinkProductFullSync(
  token: string,
  environment: "primary" | "alt",
): Promise<{
  environment: "primary" | "alt";
  count: number;
  items: SmartLinkProductPreviewItem[];
  unsupported?: Record<string, number>;
}> {
  return authenticatedProApiFetch("/smart-link/products/full-sync/preview", token, {
    method: "POST",
    body: JSON.stringify({ environment }),
  });
}

export interface SmartLinkProductCommitResult {
  requested: number;
  created: number;
  linked: number;
  skipped: Array<{ smartProductId: string; reason: "archived" | "conflict" | "needs_model" | "error"; detail?: string }>;
}

export async function commitSmartLinkProductFullSync(
  token: string,
  body: {
    environment: "primary" | "alt";
    organizationId: string | null;
    smartProductIds: string[];
    modelOverrides: Record<string, string>;
    includeArchived: boolean;
  },
): Promise<SmartLinkProductCommitResult> {
  return authenticatedProApiFetch("/smart-link/products/full-sync/commit", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface SmartLinkProductComparison {
  link: Omit<SmartLinkProductLink, "product">;
  smartFetchedLive: boolean;
  smartError: string | null;
  status: SmartLinkSyncStatus;
  fields: Array<{ field: string; proValue: unknown; smartValue: unknown; matches: boolean }>;
}

const productPath = (productId: string) => `/smart-link/products/by-product/${encodeURIComponent(productId)}`;

export async function fetchSmartLinkProductComparison(
  token: string,
  productId: string,
): Promise<SmartLinkProductComparison> {
  return authenticatedProApiFetch(`${productPath(productId)}/compare`, token);
}

export async function syncSmartLinkProductFromSmart(
  token: string,
  productId: string,
): Promise<SmartLinkProductComparison> {
  return authenticatedProApiFetch(`${productPath(productId)}/sync-from-smart`, token, { method: "POST" });
}

export async function syncSmartLinkProductFromPro(
  token: string,
  productId: string,
): Promise<SmartLinkProductComparison> {
  return authenticatedProApiFetch(`${productPath(productId)}/sync-from-pro`, token, { method: "POST" });
}

export async function setSmartLinkProductEnabled(
  token: string,
  productId: string,
  enabled: boolean,
): Promise<{ linkEnabled: boolean; linkDisabledAt: string | null }> {
  return authenticatedProApiFetch(`${productPath(productId)}/link-enabled`, token, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });
}
